import { useEffect } from 'react';
import { useQuote } from '@/features/quote/context/QuoteContext';
import { isLiveQuote } from '@/features/quote/utils/quoteStatus';
import { useAvailableAddOns } from './useAddOnsQuery';
import { reconcileAutoIncludedAddOns, loadBaselineDismissals } from '../utils/autoIncludedAddOns';
import { instanceIdMinter } from '../utils/instanceIds';

export interface AutoIncludedAddOnsState {
    /** The configuration step blocks its advance on this: a save now would omit a seeded line. */
    assignmentsPending: boolean;
}

// Seeds auto-included lines on product load and services change, and
// baselines dismissals once per quote load before reconciling.
export const useAutoIncludedAddOns = (): AutoIncludedAddOnsState => {
    const {
        status,
        quoteId,
        currentStep,
        saveLane,
        selectedProduct,
        configuredServices,
        configuredAddOns,
        dismissedAutoAddOnIds,
        autoAddOnBaselinePending,
        updateAddOns,
        applyAutoAddOnBaseline,
    } = useQuote();
    const { requestSave } = saveLane;
    const { addons, isSuccess, isPlaceholderData, error } = useAvailableAddOns(selectedProduct?.id);
    const assignmentsResolved = isSuccess && !isPlaceholderData;
    // A completed or dead quote is a record of what was sold, not a live configuration.
    const quoteIsLive = isLiveQuote(status);
    // A failed load never blocks the journey: the reconciler cannot seed from it either.
    const assignmentsPending =
        quoteIsLive && Boolean(selectedProduct) && !assignmentsResolved && !error;

    useEffect(() => {
        if (!quoteIsLive) return;
        if (!assignmentsResolved) return;

        if (autoAddOnBaselinePending) {
            applyAutoAddOnBaseline(
                loadBaselineDismissals(addons, configuredServices, configuredAddOns)
            );
            return;
        }

        const next = reconcileAutoIncludedAddOns({
            assignments: addons,
            services: configuredServices,
            addOns: configuredAddOns,
            dismissedAutoAddOnIds: dismissedAutoAddOnIds ?? [],
            nextInstanceId: instanceIdMinter(configuredAddOns),
        });
        if (next !== null) {
            updateAddOns(next);
            if (currentStep === 'summary' && quoteId) {
                requestSave({ quiet: true });
            }
        }
    }, [
        addons,
        assignmentsResolved,
        quoteIsLive,
        configuredServices,
        configuredAddOns,
        dismissedAutoAddOnIds,
        autoAddOnBaselinePending,
        updateAddOns,
        applyAutoAddOnBaseline,
        currentStep,
        quoteId,
        requestSave,
    ]);

    return { assignmentsPending };
};
